import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInfiniteQuery } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableSkeleton } from '@/components/common/skeleton'
import { ErrorState } from '@/components/common/error-state'
import { EmptyState } from '@/components/common/empty-state'

const EVENT_NAMES = [
  '',
  'Register',
  'CompleteRegistration',
  'Deposit',
  'FirstDeposit',
  'BTDeposit',
  'BTFirstDeposit',
  'Purchase',
]

export function EventsPage() {
  const navigate = useNavigate()
  const [eventName, setEventName] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')

  const query = useInfiniteQuery({
    queryKey: ['events', eventName, since, until],
    queryFn: ({ pageParam }) =>
      api.getEvents({
        event_name: eventName || undefined,
        since: since || undefined,
        until: until || undefined,
        limit: 50,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.has_more ? last.cursor ?? undefined : undefined),
  })

  const items = query.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Events</h2>
        <p className="text-sm text-muted-foreground">
          Mirrored CAPI events from the attribution pipeline
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[180px] space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Event name</label>
              <Select
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
              >
                <option value="">All events</option>
                {EVENT_NAMES.filter(Boolean).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Since</label>
              <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Until</label>
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={8} />
            </div>
          ) : query.isError ? (
            <div className="p-6">
              <ErrorState message={query.error.message} onRetry={() => query.refetch()} />
            </div>
          ) : items.length === 0 ? (
            <div className="p-6">
              <EmptyState message="No events match your filters." />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Event ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((event) => (
                    <TableRow
                      key={event.event_id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/events/${event.event_id}`)}
                    >
                      <TableCell>
                        <Badge variant="outline">{event.event_name}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{event.brand_id}</TableCell>
                      <TableCell>
                        {/* The API returns username only to admins (server-gated),
                            so show it whenever present; else the masked fn. */}
                        {event.username ? event.username : event.username_masked}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(event.received_at)}
                      </TableCell>
                      <TableCell className="max-w-[140px] truncate font-mono text-xs">
                        {event.event_id}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {query.hasNextPage && (
                <div className="border-t p-4 text-center">
                  <Button
                    variant="outline"
                    onClick={() => query.fetchNextPage()}
                    disabled={query.isFetchingNextPage}
                  >
                    {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
